import React, { useState, useEffect } from "react";
import { Card, Form, Space, Typography, Button, Alert, Spin } from "antd";
import type { UploadFile } from "antd/es/upload";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import RobinPage from "../components/RobinPage";
import { FinanceTransactionForm } from "../components/FinanceTransactionForm";
import { useFinanceTreeItems } from "../hooks/useFinanceTreeItems";
import { useFinanceTransaction } from "../hooks/useFinanceTransaction";
import { ArrowLeftOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { TransactionLine } from "../types/financeTransaction.types";

const { Text } = Typography;

function FinanceTransactionView() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();

  // State
  const [transactionLines, setTransactionLines] = useState<TransactionLine[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // Fetch finance tree items
  const { data: financeTreeData, loading: financeTreeLoading } = useFinanceTreeItems();
  
  // Fetch transaction data
  const { data: transactionData, loading } = useFinanceTransaction(parseInt(id || "0"));

  // Load transaction data into form
  useEffect(() => {
    if (transactionData?.getFinanceTreeTransaction) {
      const transaction = transactionData.getFinanceTreeTransaction;
      
      // Set form values
      form.setFieldsValue({
        date: transaction.date ? dayjs(transaction.date) : null,
        description: transaction.description || "",
      });

      // Set transaction lines
      const lines: TransactionLine[] = transaction.transactionLines.map((line, index) => ({
        key: line.id.toString(),
        financeItemId: line.financeTreeId,
        description: line.description || "",
        debit: line.debit,
        credit: line.credit,
      }));
      setTransactionLines(lines);

      // Set file list if image exists
      if (transaction.image) {
        const uploadFile: UploadFile = {
          uid: transaction.image.id.toString(),
          name: transaction.image.originalName,
          status: "done",
          url: transaction.image.url,
          type: transaction.image.mimeType,
          size: transaction.image.size,
        };
        setFileList([uploadFile]);
      }
    }
  }, [transactionData, form]);

  // Get finance tree items from response
  const financeTreeItems = financeTreeData?.getAllFinanceTreeItems || [];

  // Calculate level based on serialNumber (count dots)
  const calculateLevel = (serialNumber: string): number => {
    return (serialNumber.match(/\./g) || []).length;
  };

  // Create indented options for the select dropdown
  const financeTreeOptions = financeTreeItems.map((item) => {
    const level = calculateLevel(item.serialNumber);
    return {
      label: `${"  ".repeat(level)}${item.serialNumber} - ${currentLang === "ar" ? item.nameAr : item.name}`,
      value: item.id,
      level: level,
    };
  });

  // Dummy handlers for view mode
  const handleCustomUpload = async () => {};
  const handleRemove = async (file: any) => {};
  const handleSubmit = () => {};
  const handleCancel = () => {
    navigate("/finance/transactions");
  };

  if (loading) {
    return (
      <RobinPage title={t("label.finance_transaction.view_title")}>
        <Card
          bordered={false}
          style={{
            borderRadius: 12,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            textAlign: "center",
            padding: "80px 0",
          }}
        >
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">{t("label.loading")}</Text>
          </div>
        </Card>
      </RobinPage>
    );
  }

  return (
    <RobinPage
      title={t("label.finance_transaction.view_title")}
      actions={
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/finance/transactions")}>
            {t("button.back")}
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/finance/transactions/edit/${id}`)}
          >
            {t("button.edit")}
          </Button>
        </Space>
      }
    >
      <Alert
        message={t("label.finance_transaction.view_mode_info")}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Card
        bordered={false}
        style={{
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
        title={
          <Space direction="vertical" size={0}>
            <Text strong style={{ fontSize: 16 }}>
              {t("label.finance_transaction.view_title")}
            </Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t("label.finance_transaction_description")}
            </Text>
          </Space>
        }
      >
        <FinanceTransactionForm
          form={form}
          transactionLines={transactionLines}
          setTransactionLines={setTransactionLines}
          financeTreeOptions={financeTreeOptions}
          financeTreeLoading={financeTreeLoading}
          fileList={fileList}
          setFileList={setFileList}
          handleCustomUpload={handleCustomUpload}
          handleRemove={handleRemove}
          uploading={false}
          isViewMode={true}
          isEditMode={false}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          saving={false}
        />
      </Card>
    </RobinPage>
  );
}

export default FinanceTransactionView;

